// AgendaList — mobile-friendly calendar view. Standard month grid doesn't
// translate to phone screens (7 cells wide at 375px = ~50px each = unusable).
// Pattern matches Google Calendar / Loomly mobile: vertical list of upcoming
// posts grouped by day. Empty days are skipped to keep the list focused.
//
// Re-uses the TileCard component + groupTilesForDay grouper from MonthGrid
// so cards look identical on mobile and desktop.

import type { ScheduledPost } from '@/lib/api';
import type { Post } from '@/lib/gallery';
import { TileCard, groupTilesForDay, type DayCell } from './MonthGrid';

interface AgendaListProps {
  cells: DayCell[];
  postsById: Record<string, Post>;
  postImageUrls?: Record<string, string>;
  onDayClick: (date: Date) => void;
  onTileClick: (tile: ScheduledPost) => void;
  onTileDelete: (tile: ScheduledPost) => void;
}

export default function AgendaList({
  cells,
  postsById,
  postImageUrls,
  onDayClick,
  onTileClick,
  onTileDelete,
}: AgendaListProps) {
  // Only show in-month days that actually have events. Empty days are
  // omitted to keep the list scannable; users can hit the global Ad
  // Scheduler / use a long-press on the date header to add (future).
  const daysWithEvents = cells.filter(
    (c) => c.inMonth && c.tiles.length > 0,
  );

  if (daysWithEvents.length === 0) {
    return (
      <div className="rounded-2xl glass-stroke bg-white/85 px-5 py-8 text-center">
        <i className="ri-calendar-event-line text-3xl text-dark-muted/60" />
        <p className="font-display font-bold text-dark text-base mt-3">
          No posts scheduled this month
        </p>
        <p className="text-sm text-dark-muted mt-1">
          Use Ad Pilot to bulk-fill, or schedule a single post from My Creatives.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {daysWithEvents.map((cell) => (
        <DaySection
          key={cell.key}
          cell={cell}
          postsById={postsById}
          postImageUrls={postImageUrls}
          onDayClick={onDayClick}
          onTileClick={onTileClick}
          onTileDelete={onTileDelete}
        />
      ))}
    </div>
  );
}

function DaySection({
  cell,
  postsById,
  postImageUrls,
  onDayClick,
  onTileClick,
  onTileDelete,
}: {
  cell: DayCell;
  postsById: Record<string, Post>;
  postImageUrls?: Record<string, string>;
  onDayClick: (date: Date) => void;
  onTileClick: (tile: ScheduledPost) => void;
  onTileDelete: (tile: ScheduledPost) => void;
}) {
  const groups = groupTilesForDay(cell.tiles, postsById);
  const dayLabel = cell.date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const isPast = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return cell.date < today;
  })();

  return (
    <div>
      {/* Day header — date badge + label, with quick-add affordance on
          the right for non-past days. */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full flex-shrink-0 ${
              cell.isToday
                ? 'bg-primary text-white'
                : 'bg-surface-dark/30 text-dark'
            }`}
          >
            {cell.date.getDate()}
          </span>
          <div className="flex items-center gap-2 min-w-0">
            <span className={`font-display font-bold text-sm truncate ${
              cell.isToday ? 'text-primary' : 'text-dark'
            }`}>
              {dayLabel}
            </span>
            {cell.isToday && (
              <span className="px-2 py-0.5 rounded-full bg-primary text-white text-xs font-bold flex-shrink-0">
                Today
              </span>
            )}
          </div>
        </div>
        {!isPast && (
          <button
            type="button"
            onClick={() => onDayClick(cell.date)}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white flex items-center justify-center transition-all cursor-pointer"
            title="Schedule a post for this day"
            aria-label="Schedule a post"
          >
            <i className="ri-add-line text-sm" />
          </button>
        )}
      </div>

      {/* Cards stack — same TileCard component used in the desktop month
          grid, no responsive logic needed (cards are content-sized). */}
      <div className="space-y-2">
        {groups.map((group) => (
          <TileCard
            key={group.groupKey}
            group={group}
            postImageUrls={postImageUrls}
            onClick={() => onTileClick(group.primary)}
            onDelete={() => onTileDelete(group.primary)}
          />
        ))}
      </div>
    </div>
  );
}
